import { useEffect, useMemo, useRef, useState } from 'react'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'

/**
 * Pages this billing period: the running total so far (solid), a straight-line forecast to the last day
 * (dashed), and the plan's included pages as a reference line. One y-axis, in pages. The forecast end is
 * the server's projection when it has one, so the chart and the estimated-spend card agree.
 */

export interface UsageDay { day: string; pages: number; documents: number }

const DAY_MS = 86_400_000
const HEIGHT = 220
const M = { top: 20, right: 20, bottom: 28, left: 52 }

/** yyyy-mm-dd as a UTC day number. Period dates are calendar dates in UTC (the billing month). */
function dayNumber(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d) / DAY_MS
}

function label(dayNum: number): string {
  return new Date(dayNum * DAY_MS).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function compact(n: number): string {
  return n >= 10_000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : `${n}`
}

/** A round axis maximum just above the data, so gridlines land on readable numbers. */
function niceMax(v: number): number {
  const target = Math.max(10, v)
  const pow = 10 ** Math.floor(Math.log10(target))
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (step * pow >= target) return step * pow
  return 10 * pow
}

function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(fallback)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(240, Math.round(e.contentRect.width))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

export interface ForecastModel {
  days: number
  todayIndex: number
  /** Running page total at the end of each day so far (index 0 = the 1st). */
  cumulative: number[]
  perDay: UsageDay[]
  forecastEnd: number | null
}

/** Pure: lays the period out by day. Exported for tests. */
export function forecastModel(periodStart: string, periodEnd: string, daily: UsageDay[], projectedPages: number | null, now: Date,
  usageFrom?: string | null): ForecastModel {
  const start = dayNumber(periodStart)
  const days = Math.max(1, dayNumber(periodEnd) - start)
  // Same as the server: a team created mid-period is measured from its creation, not from the 1st.
  const from = Math.max(start, usageFrom ? new Date(usageFrom).getTime() / DAY_MS : start)
  const nowDay = now.getTime() / DAY_MS
  const elapsed = Math.min(1, Math.max(0, (nowDay - from) / (start + days - from)))
  const todayIndex = Math.min(days - 1, Math.max(0, Math.floor(now.getTime() / DAY_MS) - start))

  const byIndex = new Map(daily.map(d => [dayNumber(d.day) - start, d]))
  const perDay: UsageDay[] = []
  const cumulative: number[] = []
  let total = 0
  for (let i = 0; i <= todayIndex; i++) {
    const d = byIndex.get(i) ?? { day: new Date((start + i) * DAY_MS).toISOString().slice(0, 10), pages: 0, documents: 0 }
    total += d.pages
    perDay.push(d)
    cumulative.push(total)
  }
  // Same rule as the server's projection: no extrapolating from the first day's few hours.
  const forecastEnd = projectedPages ?? (nowDay - from >= days / 31 && elapsed > 0 ? Math.round(total / elapsed) : null)
  return { days, todayIndex, cumulative, perDay, forecastEnd }
}

export default function UsageForecastChart({ periodStart, periodEnd, daily, included, projectedPages, usageFrom, now = new Date() }: {
  periodStart: string
  periodEnd: string
  usageFrom?: string | null
  daily: UsageDay[]
  included: number | null
  projectedPages: number | null
  now?: Date
}) {
  const [wrapRef, width] = useWidth<HTMLDivElement>(600)
  const [hover, setHover] = useState<number | null>(null)
  const model = useMemo(
    () => forecastModel(periodStart, periodEnd, daily, projectedPages, now, usageFrom),
    // `now` is read once per render of the page; a new Date each render must not rebuild the model.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodStart, periodEnd, daily, projectedPages, usageFrom],
  )
  const { days, todayIndex, cumulative, perDay, forecastEnd } = model
  const start = dayNumber(periodStart)
  const soFar = cumulative[todayIndex] ?? 0

  const yMax = niceMax(Math.max(soFar, forecastEnd ?? 0, included ?? 0) * 1.08)
  const plotW = width - M.left - M.right
  const plotH = HEIGHT - M.top - M.bottom
  const x = (i: number) => M.left + (days === 1 ? 0 : (i / (days - 1)) * plotW)
  const y = (v: number) => M.top + plotH - (v / yMax) * plotH

  const actualPath = cumulative.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ')
  const areaPath = `${actualPath} L${x(todayIndex)},${y(0)} L${x(0)},${y(0)} Z`
  const showForecast = forecastEnd != null && todayIndex < days - 1
  const ticks = [0.25, 0.5, 0.75, 1].map(f => f * yMax)
  const xTicks = Array.from({ length: days }, (_, i) => i).filter(i => i === 0 || i === days - 1 || (i % 7 === 0 && days - 1 - i >= 3))

  const forecastAt = (i: number) =>
    forecastEnd == null ? null : Math.round(soFar + ((forecastEnd - soFar) * (i - todayIndex)) / Math.max(1, days - 1 - todayIndex))

  function onMove(e: React.MouseEvent<SVGRectElement>) {
    const box = e.currentTarget.getBoundingClientRect()
    const i = Math.round(((e.clientX - box.left) / box.width) * (days - 1))
    setHover(Math.min(days - 1, Math.max(0, i)))
  }

  const tip = hover == null ? null : hover <= todayIndex
    ? { title: label(start + hover), lines: [
        `${perDay[hover].pages.toLocaleString()} pages · ${perDay[hover].documents.toLocaleString()} documents`,
        `${cumulative[hover].toLocaleString()} pages so far`,
      ] }
    : forecastAt(hover) != null
      ? { title: label(start + hover), lines: [`Forecast: about ${forecastAt(hover)!.toLocaleString()} pages by this day`] }
      : null

  return (
    <div data-testid="usage-forecast-chart">
      <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
        <svg width={width} height={HEIGHT} role="img" style={{ display: 'block', overflow: 'visible' }}
          aria-label={`${soFar.toLocaleString()} pages so far this period${forecastEnd != null ? `, forecast about ${forecastEnd.toLocaleString()} by ${label(start + days - 1)}` : ''}${included != null ? `, ${included.toLocaleString()} included` : ''}.`}>
          {ticks.map(t => (
            <g key={t}>
              <line x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} style={{ stroke: colors.border }} strokeWidth={1} />
              <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end" style={{ fill: colors.textMuted, fontSize: fontSize['2xs'] }}>{compact(t)}</text>
            </g>
          ))}
          <line x1={M.left} x2={width - M.right} y1={y(0)} y2={y(0)} style={{ stroke: colors.textMuted }} strokeWidth={1} />
          {xTicks.map(i => (
            <text key={i} x={x(i)} y={HEIGHT - 8} textAnchor={i === 0 ? 'start' : i === days - 1 ? 'end' : 'middle'}
              style={{ fill: colors.textMuted, fontSize: fontSize['2xs'] }}>{label(start + i)}</text>
          ))}

          {included != null && (
            <g data-testid="included-line">
              <line x1={M.left} x2={width - M.right} y1={y(included)} y2={y(included)}
                style={{ stroke: colors.textSecondary }} strokeWidth={1.5} strokeDasharray="2 4" />
              {/* Left end: the forecast's end label sits at the right, often near this line. */}
              <text x={M.left + 4} y={y(included) - 6} textAnchor="start" style={{ fill: colors.textSecondary, fontSize: fontSize['2xs'], fontWeight: 600 }}>
                {included.toLocaleString()} included
              </text>
            </g>
          )}

          <path d={areaPath} style={{ fill: colors.primary, opacity: 0.1 }} />
          {showForecast && (
            <line data-testid="forecast-line" x1={x(todayIndex)} y1={y(soFar)} x2={x(days - 1)} y2={y(forecastEnd!)}
              style={{ stroke: colors.primary }} strokeWidth={2} strokeDasharray="6 5" strokeLinecap="round" opacity={0.7} />
          )}
          <path d={actualPath} fill="none" style={{ stroke: colors.primary }} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          <circle cx={x(todayIndex)} cy={y(soFar)} r={4.5} style={{ fill: colors.primary, stroke: colors.surface }} strokeWidth={2} />
          {showForecast && (
            <>
              <circle cx={x(days - 1)} cy={y(forecastEnd!)} r={4.5} style={{ fill: colors.surface, stroke: colors.primary }} strokeWidth={2} />
              <text x={x(days - 1)} y={y(forecastEnd!) - 10} textAnchor="end" style={{ fill: colors.text, fontSize: fontSize.xs, fontWeight: 600 }}>
                ~{forecastEnd!.toLocaleString()}
              </text>
            </>
          )}

          {hover != null && (
            <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={y(0)} style={{ stroke: colors.textMuted }} strokeWidth={1} />
          )}
          <rect data-testid="chart-hit" x={M.left} y={M.top} width={Math.max(1, plotW)} height={plotH} fill="transparent"
            onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
        </svg>

        {tip && hover != null && (
          <div role="tooltip" style={{
            position: 'absolute', top: M.top, left: Math.min(Math.max(0, x(hover) + 10), width - 200), pointerEvents: 'none',
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxShadow: shadow.md,
            padding: `${spacing.xs} ${spacing.sm}`, fontSize: fontSize.xs, color: colors.text, minWidth: 160,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{tip.title}</div>
            {tip.lines.map(l => <div key={l} style={{ color: colors.textSecondary }}>{l}</div>)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap', marginTop: spacing.sm, fontSize: fontSize.xs, color: colors.textSecondary }}>
        <Swatch dash={false} color={colors.primary}>Pages so far</Swatch>
        {showForecast && <Swatch dash color={colors.primary}>Forecast (straight line from usage so far)</Swatch>}
        {included != null && <Swatch dash color={colors.textSecondary}>Included in your plan</Swatch>}
      </div>

      <details style={{ marginTop: spacing.sm, fontSize: fontSize.xs, color: colors.textSecondary }}>
        <summary style={{ cursor: 'pointer' }}>Daily figures</summary>
        <table style={{ marginTop: spacing.xs, borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr><th style={th}>Day</th><th style={th}>Pages</th><th style={th}>Documents</th><th style={th}>Running total</th></tr>
          </thead>
          <tbody>
            {perDay.map((d, i) => (
              <tr key={d.day}>
                <td style={td}>{label(start + i)}</td>
                <td style={tdNum}>{d.pages.toLocaleString()}</td>
                <td style={tdNum}>{d.documents.toLocaleString()}</td>
                <td style={tdNum}>{cumulative[i].toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', fontWeight: 600, padding: '2px 12px 2px 0' }
const td: React.CSSProperties = { padding: '2px 12px 2px 0' }
const tdNum: React.CSSProperties = { ...td, textAlign: 'right' }

function Swatch({ dash, color, children }: { dash: boolean; color: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <svg width={18} height={6} aria-hidden="true">
        <line x1={1} x2={17} y1={3} y2={3} style={{ stroke: color }} strokeWidth={2} strokeDasharray={dash ? '4 3' : undefined} strokeLinecap="round" />
      </svg>
      {children}
    </span>
  )
}
